import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { InviteResult, Responsible } from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";

interface Station {
  id: number;
  name: string;
  city: string;
  timezone: string;
}

export default function Responsibles() {
  const { t } = useT();
  const [items, setItems] = useState<Responsible[] | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [invite, setInvite] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [editingStation, setEditingStation] = useState<number | null>(null);
  const [stationForm, setStationForm] = useState({ name: "", city: "", timezone: "Europe/Kyiv" });
  const [showCreateStation, setShowCreateStation] = useState(false);

  const load = () => {
    setError(null);
    Promise.all([
      api.get<Responsible[]>("/api/responsibles"),
      api.get<Station[]>("/api/stations"),
    ])
      .then(([rs, ss]) => { setItems(rs); setStations(ss); })
      .catch((e: ApiError) => setError(e.message));
  };
  useEffect(load, []);

  const toggle = (r: Responsible) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    api.post<Responsible>(`/api/responsibles/${r.id}/toggle`)
      .then(() => load())
      .catch((e: ApiError) => setError(e.message));
  };

  const changeStation = async (r: Responsible, stationId: number | null) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      await api.patch<Responsible>(`/api/responsibles/${r.id}/station`, { station_id: stationId });
      load();
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const createInvite = () => {
    const name = newName.trim();
    if (!name) return;
    api.post<InviteResult>("/api/invites", { full_name: name })
      .then(r => { setInvite(r); setNewName(""); setCreating(false); setCopied(false); })
      .catch((e: ApiError) => setError(e.message));
  };

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const copyInviteUrl = async (url: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        tg()?.HapticFeedback?.notificationOccurred("success");
        return;
      }
      throw new Error("clipboard unavailable");
    } catch {
      tg()?.HapticFeedback?.notificationOccurred("error");
      tg()?.showAlert?.(t("people.invite.copy_failed"));
    }
  };

  const startEditStation = (s: Station) => {
    setEditingStation(s.id);
    setStationForm({ name: s.name, city: s.city, timezone: s.timezone });
  };

  const saveStation = async (id: number) => {
    try {
      await api.patch<Station>(`/api/stations/${id}`, stationForm);
      setEditingStation(null);
      load();
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) {
      setError((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    }
  };

  const deleteStation = (s: Station) => {
    const question = t("people.station.confirm_delete", { name: s.name });
    const tgWeb = tg();
    const doIt = async () => {
      try {
        await api.delete(`/api/stations/${s.id}`);
        load();
        tg()?.HapticFeedback?.notificationOccurred("success");
      } catch (e) {
        setError((e as ApiError).message);
        tg()?.HapticFeedback?.notificationOccurred("error");
      }
    };
    if (tgWeb?.showConfirm) {
      tgWeb.showConfirm(question, (ok) => { if (ok) doIt(); });
    } else if (window.confirm(question)) {
      doIt();
    }
  };

  const createStation = async () => {
    const name = stationForm.name.trim();
    const city = stationForm.city.trim();
    if (!name || !city) {
      setError(t("people.station.fill_required"));
      return;
    }
    try {
      await api.post<Station>("/api/stations", stationForm);
      setShowCreateStation(false);
      setStationForm({ name: "", city: "", timezone: "Europe/Kyiv" });
      load();
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) {
      setError((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    }
  };

  return (
    <div>
      <h1>{t("people.title")}</h1>

      {error && <div className="alert error">{error}</div>}

      <h2>{t("people.stations", { n: stations.length })}</h2>
      <ul className="list">
        {stations.map(s => (
          <li key={s.id} className="list-item" style={{ flexDirection: "column", alignItems: "stretch" }}>
            {editingStation === s.id ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <input value={stationForm.name} onChange={e => setStationForm({ ...stationForm, name: e.target.value })} placeholder={t("people.station.name")} />
                <input value={stationForm.city} onChange={e => setStationForm({ ...stationForm, city: e.target.value })} placeholder={t("people.station.city")} />
                <input value={stationForm.timezone} onChange={e => setStationForm({ ...stationForm, timezone: e.target.value })} placeholder={t("people.station.tz")} />
                <div className="row">
                  <button className="btn" onClick={() => saveStation(s.id)}>{t("common.save")}</button>
                  <button className="btn secondary" onClick={() => setEditingStation(null)}>{t("common.cancel")}</button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className="grow">
                  <div><b>{s.name}</b></div>
                  <div className="meta">{s.city} · {s.timezone}</div>
                </div>
                <button className="icon-btn" aria-label={t("common.edit")} onClick={() => startEditStation(s)}>✏</button>
                <button className="icon-btn destructive" aria-label={t("common.delete")} onClick={() => deleteStation(s)}>🗑</button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {showCreateStation ? (
        <div className="card">
          <h3 style={{ margin: "0 0 8px" }}>{t("people.station.new_title")}</h3>
          <input value={stationForm.name} onChange={e => setStationForm({ ...stationForm, name: e.target.value })} placeholder={t("people.station.name_placeholder")} style={{ marginBottom: 6 }} />
          <input value={stationForm.city} onChange={e => setStationForm({ ...stationForm, city: e.target.value })} placeholder={t("people.station.city")} style={{ marginBottom: 6 }} />
          <input value={stationForm.timezone} onChange={e => setStationForm({ ...stationForm, timezone: e.target.value })} placeholder={t("people.station.tz")} style={{ marginBottom: 8 }} />
          <div className="row">
            <button className="btn" onClick={createStation}>{t("common.create")}</button>
            <button className="btn secondary" onClick={() => { setShowCreateStation(false); setStationForm({ name: "", city: "", timezone: "Europe/Kyiv" }); }}>{t("common.cancel")}</button>
          </div>
        </div>
      ) : (
        <button className="btn secondary" onClick={() => setShowCreateStation(true)} style={{ marginBottom: 16 }}>
          {t("people.add_station")}
        </button>
      )}

      {invite && (
        <div className="alert success">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {t("people.invite.created_for", { name: invite.full_name })}
          </div>
          <div className="copy-block">{invite.url}</div>
          <button className="btn secondary" style={{ marginTop: 8 }} onClick={() => copyInviteUrl(invite.url)}>
            {copied ? "✅ " + t("common.copied") : t("common.copy_link")}
          </button>
          <button className="btn secondary" style={{ marginTop: 4 }} onClick={() => setInvite(null)}>
            {t("common.close")}
          </button>
        </div>
      )}

      {creating ? (
        <div className="card">
          <h3 style={{ margin: "0 0 8px" }}>{t("people.invite.title")}</h3>
          <input
            placeholder={t("people.invite.placeholder")}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            autoFocus
          />
          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn" onClick={createInvite}>{t("common.create")}</button>
            <button className="btn secondary" onClick={() => setCreating(false)}>{t("common.cancel")}</button>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={() => setCreating(true)}>
          {t("people.invite.create")}
        </button>
      )}

      <h2>{t("people.list_title", { n: items?.length ?? 0 })}</h2>
      {!items && !error && <div className="empty-state"><span className="spinner" /></div>}
      {items && items.length === 0 && (
        <div className="empty-state">{t("people.list_empty")}</div>
      )}
      {items && items.length > 0 && (
        <ul className="list">
          {items.map(r => (
            <li key={r.id} className="list-item" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className="grow">
                  <div>{r.is_active ? "✅" : "🚫"} <strong>{r.full_name}</strong></div>
                  {!r.is_active && <div className="meta">{t("shifts.deactivated")}</div>}
                </div>
                <button
                  className={"period-tab" + (r.is_active ? "" : " active")}
                  onClick={() => toggle(r)}
                >
                  {r.is_active ? t("people.deactivate") : t("people.activate")}
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="meta" style={{ fontSize: 12 }}>{t("people.station_label")}</span>
                <select
                  value={r.station_id ?? ""}
                  onChange={e => changeStation(r, e.target.value === "" ? null : parseInt(e.target.value))}
                  style={{ flex: 1 }}
                >
                  <option value="">{t("people.station_none")}</option>
                  {stations.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.city})</option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
